// @givmo/design-system — Dialog (Base UI Dialog) + PopUpTemplate wrapper.
//
// 03-3c overlay tier. Two surfaces ship here:
//   1. <Dialog> — a small compound API over @base-ui/react/dialog (Root/Trigger/Portal/
//      Backdrop/Popup/Title/Description/Close). Base UI provides the accessible plumbing:
//      role="dialog", focus trap + restore, scroll-lock, Escape + outside-click dismissal (modal
//      by default). We theme the surface from the 03-3a tokens and clamp it to the viewport in
//      both axes so no dialog can grow its own controls off-screen (see <DialogContent>).
//   2. <PopUpTemplate> — ports the Flutter webapp `templates/pop_up_template.dart`: a white
//      radius-all20 card (~412px), an optional top-right close (cross) icon, a body `child`,
//      and a primary <Button> footer (label = `buttonText`). The Flutter template scrimmed
//      the page (pop_up_opener) — Base UI's modal backdrop reproduces that with --color-scrim.
//
// a11y: the Popup carries role="dialog" and traps focus; on close, focus returns to the trigger
// (Base UI finalFocus default). The barrier is a real <Dialog.Backdrop>. `testId` forwards to
// the Popup (PopUpTemplate also derives `-close`/-action` ids).
//
// ── `aria-modal` is the KIT's, and it follows the ROOT ───────────────────────────────────────
// Base UI 1.6 writes `role="dialog"` on the popup but never `aria-modal` (verified in the installed
// `@base-ui/react/dialog/popup/DialogPopup.js`: the popup props carry `role`, `aria-labelledby`
// and `aria-describedby` only), so without it a screen reader gets no signal that the page behind
// a modal card is off-limits. The attribute must state the modality the ROOT runs with — a
// non-modal `<Dialog.Root modal={false}>` whose popup claimed `aria-modal="true"` would be lying
// about the page behind it. Base UI keeps that modality in its root store, but the hook that reads
// it (`useDialogRootContext`, `@base-ui/react/dialog/root/DialogRootContext.js`) is internal: it is
// absent from `@base-ui/react/dialog`'s `index.parts` exports and unreachable through the package
// `exports` map, and `DialogPopup.State` carries `open`/`transitionStatus`/`nested`/
// `nestedDialogOpen` only. So `<Dialog.Root>` is a thin wrapper that publishes its own `modal` on a
// kit context, exactly as `<Sheet.Root>` does, and `<Dialog.Content>` derives the attribute from
// that — one source of truth, nothing for a call site to repeat or forget.
import { createContext, useContext, type ReactNode } from 'react';
import {
  Dialog as BaseDialog,
  type DialogPopupProps,
  type DialogRootProps,
} from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Button } from './button';
import { Icon } from './icon';

// ---- Low-level compound primitive (themed Base UI parts) ----------------------------------

/** Base UI's three-way modality: `true` (default), `'trap-focus'`, or `false`. */
export type DialogModality = boolean | 'trap-focus';

/**
 * The modality the surrounding `<Dialog.Root>` runs with. Defaults to Base UI's own default, so a
 * popup rendered under a bare Base UI root (or none) still reads as the modal card it is.
 */
const DialogModalityContext = createContext<DialogModality>(true);

/**
 * Groups the parts, forwarding every Base UI root prop untouched (`children` may be Base UI's
 * payload render function, so it is passed through rather than wrapped). Its one addition is
 * publishing `modal` for `<Dialog.Content>` — see the header note on `aria-modal`.
 */
function DialogRoot<Payload = unknown>(props: DialogRootProps<Payload>) {
  const { modal = true } = props;
  return (
    <DialogModalityContext.Provider value={modal}>
      <BaseDialog.Root {...props} />
    </DialogModalityContext.Provider>
  );
}
const DialogTrigger = BaseDialog.Trigger;
const DialogClose = BaseDialog.Close;
const DialogTitle = BaseDialog.Title;
const DialogDescription = BaseDialog.Description;

export interface DialogContentProps extends TestIdProps {
  children: ReactNode;
  /** Extra classes merged onto the popup card. */
  className?: string;
  /** Width of the popup card in px (Flutter PopUpTemplate uses 412). */
  width?: number;
  /**
   * Element to focus when the dialog opens, as `<Sheet.Content>` takes it. Base UI's default is the
   * first tabbable element, which in a card whose content scrolls may sit below the fold: focusing
   * it scrolls the content away from its top.
   */
  initialFocus?: DialogPopupProps['initialFocus'];
  /**
   * The popup is the whole window, with no scrim, no card and no width: a full-window flow over the
   * page (the Fundraisers creation wizard, EMC-WEB7), which paints its own surface through
   * `className`. It keeps everything the modal machinery gives a card — `role="dialog"`,
   * `aria-modal`, the focus trap, the page behind marked inert and its scroll locked — and scrolls its
   * own content, contained, on a window too short for it. `width` is ignored.
   */
  fullScreen?: boolean;
  'aria-label'?: string;
}

/**
 * Portal + scrim backdrop + themed popup card, in one part. Renders Base UI's
 * `Portal`/`Backdrop`/`Popup`. The card is the kit's white radius-all20 dialog surface.
 *
 * ★ The card is CLAMPED TO THE VIEWPORT IN BOTH AXES AND SCROLLS ITS OWN CONTENT. The height
 * clamp is the exact counterpart of the width one above it — a 16px gutter on every side — and it
 * lives HERE, not at the call sites, because it is a property of the MECHANISM: a `position:
 * fixed`, translate-centred card whose height is content-driven, over a page Base UI has
 * scroll-locked. Grow such a card past the viewport and its footer leaves the screen with nothing
 * able to bring it back — the page cannot scroll, the card did not, and the control is inside a
 * fixed box. That shipped: the employer gift-card confirm (`apps/admin`) grew past 1000px when its
 * AB 488 §317 pre-purchase disclosure landed, putting Send ~128px below the fold at a 720px
 * viewport, so no employer admin on a short window could complete a purchase.
 *
 * Height uses the DYNAMIC viewport unit while width uses the static one, deliberately: mobile
 * browser chrome changes the visible height and never the width, so the static unit would clamp to
 * a height that is not actually on screen. `overscroll-behavior: contain` keeps a touch scroll that
 * reaches either end of the card from chaining to the locked page beneath it.
 *
 * Every part of this is a DEFAULT, not a law — `className` merges last through `cn`/tailwind-merge,
 * so a surface wanting a tighter clamp, or the sticky-chrome idiom (clamp the card, scroll an inner
 * `flex-1` region so the title and footer stay put — `sponsor-dpa-section.tsx`, `trace-dialog.tsx`),
 * still gets exactly what it writes.
 */
function DialogContent({
  children,
  className,
  width = 412,
  initialFocus,
  fullScreen = false,
  testId,
  'aria-label': ariaLabel,
}: DialogContentProps) {
  // `true` and `'trap-focus'` both hide the page behind the card (Base UI traps focus and marks the
  // page inert for either), so both are announced modal; only a `modal={false}` root withholds it.
  const modal = useContext(DialogModalityContext);
  return (
    <BaseDialog.Portal>
      {/* A full-window popup covers the page itself, so a scrim under it would only be painted over. */}
      {!fullScreen && <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-scrim" />}
      <BaseDialog.Popup
        aria-label={ariaLabel}
        aria-modal={modal === false ? undefined : 'true'}
        initialFocus={initialFocus}
        style={fullScreen ? undefined : { width }}
        className={cn(
          fullScreen
            ? 'fixed inset-0 z-50 overflow-y-auto overscroll-contain outline-none'
            : [
                'fixed top-1/2 left-1/2 z-50 max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2',
                'max-h-[calc(100dvh-32px)] overflow-y-auto overscroll-contain',
                'rounded-all20 bg-white p-24 shadow-soft outline-none',
              ],
          className,
        )}
        {...testIdProp(testId)}
      >
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}

/** Compound Dialog API: `<Dialog.Root>…<Dialog.Content>`. */
export const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Content: DialogContent,
  Close: DialogClose,
  Title: DialogTitle,
  Description: DialogDescription,
};

// ---- PopUpTemplate (ports pop_up_template.dart) -------------------------------------------

export interface PopUpTemplateProps extends TestIdProps {
  /** Open state (controlled). */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Body content rendered between the close row and the footer button. */
  children: ReactNode;
  /** Footer primary-button label (Flutter `buttonText`). */
  buttonText: string;
  /** Footer button handler (Flutter `onTap`). */
  onAction?: () => void;
  /**
   * Close the dialog after the action runs (Flutter `doNavigatorPop`, default true).
   */
  closeOnAction?: boolean;
  /** Footer button busy state (Flutter `isLoading`). */
  loading?: boolean;
  /** Footer button enabled state (Flutter `isActive`, default true). */
  active?: boolean;
  /** Render the top-right close (cross) icon (Flutter `showCloseIcon`, default true). */
  showCloseIcon?: boolean;
  /** Accessible label for the dialog when there's no rendered <Dialog.Title>. */
  'aria-label'?: string;
  className?: string;
}

/**
 * The Flutter `PopUpTemplate` ported to a Base UI modal dialog: a white radius-all20 card
 * with an optional top-right close icon, a body `child`, and a footer primary Button.
 */
export function PopUpTemplate({
  open,
  defaultOpen,
  onOpenChange,
  children,
  buttonText,
  onAction,
  closeOnAction = true,
  loading = false,
  active = true,
  showCloseIcon = true,
  'aria-label': ariaLabel = 'Dialog',
  className,
  testId,
}: PopUpTemplateProps) {
  return (
    <Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={ariaLabel}
        className={cn('flex flex-col gap-4 pt-10 pb-36', className)}
        testId={testId}
      >
        {/* Close row (Flutter centerRight cross icon; reserved height when hidden). */}
        <div className="flex h-24 items-center justify-end">
          {showCloseIcon && (
            <Dialog.Close
              className="rounded-all100 text-gray80 outline-none focus-visible:shadow-focus-glow"
              aria-label="Close"
              {...testIdProp(testId ? `${testId}-close` : undefined)}
            >
              <Icon icon={X} size={24} aria-hidden />
            </Dialog.Close>
          )}
        </div>
        {children}
        {/*
         * Footer primary button. When `closeOnAction`, render it THROUGH <Dialog.Close> so the
         * dialog closes natively in both controlled and uncontrolled modes (and focus restores
         * to the trigger); the supplied `onAction` still runs on the same click. Otherwise it's
         * a plain button that leaves the dialog open.
         */}
        {closeOnAction ? (
          <Dialog.Close
            disabled={!active || loading}
            render={
              <Button
                variant="round"
                loading={loading}
                disabled={!active}
                onClick={() => onAction?.()}
                testId={testId ? `${testId}-action` : undefined}
              >
                {buttonText}
              </Button>
            }
          />
        ) : (
          <Button
            variant="round"
            loading={loading}
            disabled={!active}
            onClick={() => onAction?.()}
            testId={testId ? `${testId}-action` : undefined}
          >
            {buttonText}
          </Button>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
